import Vue from 'vue';
import _throttle from 'lodash/throttle';
import _cloneDeep from 'lodash/cloneDeep';

const REF_SEPARATOR = ':';
const REF_ATTR = 'data-component-ref';
const REF_PROPS_ATTR = 'data-component-ref-props';

const parseRefAttr = (el) => {
  const attr = el.getAttribute(REF_ATTR);
  const parts = attr.split(REF_SEPARATOR);
  const [componentName, refKey] = parts;

  return {
    componentName,
    refKey,
  };
};

class Component {
  static isComponent = true;

  constructor(el, props, name, app) {
    this.$el = el;
    this.$app = app;
    this.$name = name;
    this.$props = props || {};
    this.$refs = {};
    this.$listeners = {};
    this.$observer = null;
    this.$initialized = false;

    this.$initObserver();
  }

  $init() {
    this.$refs = this.$getRefs();

    if (typeof this.onInit === 'function') {
      this.onInit();
    }

    this.$initialized = true;
    this.$emit('init', this);
  }

  $destroy() {
    if (typeof this.onDestroy === 'function') {
      this.onDestroy();
    }

    if (this.$observer) {
      this.$observer.disconnect();
      this.$observer = null;
    }

    this.$initialized = false;
    this.$emit('destroy', this);
  }

  $initVueComponent(config, el) {
    if (!el) {
      el = document.createElement('div');
      this.$el.appendChild(el);
    }
    return new Vue({
      el,
      ...config,
    });
  }

  $destroyVueComponent(component, key) {
    const vueComponent = component || key ? this[key] : null;
    if (!vueComponent) return;

    vueComponent.$destroy();
    vueComponent.$el.parentElement.removeChild(vueComponent.$el);

    if (key) {
      this[key] = null;
    }
  }

  $updateRefs() {
    const oldRefs = _cloneDeep(this.$refs);
    this.$refs = this.$getRefs();

    if (typeof this.onUpdateRefs === 'function') {
      this.onUpdateRefs(this.$refs, oldRefs);
    }
  }

  $getRefs(key) {
    let query = `${this.$name}${REF_SEPARATOR}`;

    if (key) {
      query += key;
    }

    const refEls = this.$el.querySelectorAll(`[${REF_ATTR}^="${query}"]`);

    if (!refEls.length) {
      if (key) return null;
      return {};
    }

    const refs = {};

    Array.prototype.forEach.call(refEls, (el) => {
      const { refKey } = parseRefAttr(el);

      if (refs[refKey]) {
        if (!Array.isArray(refs[refKey])) {
          refs[refKey] = [refs[refKey]];
        }

        refs[refKey].push(el);
      } else {
        refs[refKey] = el;
      }
    });

    if (key) return refs[key];

    return refs;
  }

  $getRefProps(ref) {
    const props = ref.getAttribute(REF_PROPS_ATTR);

    try {
      return JSON.parse(props);
    } catch (err) { /* do nothing */ }

    return props;
  }

  $emit(event, data) {
    if (this.$listeners[event]) {
      this.$listeners[event].forEach((handler) => handler(data));
    }

    const domEvent = new CustomEvent(event, { detail: data });

    this.$el.dispatchEvent(domEvent);
  }

  $on(event, handler) {
    if (typeof handler !== 'function') {
      throw new TypeError('Handler must be function');
    }

    if (!this.$listeners[event]) {
      this.$listeners[event] = [];
    }

    if (!this.$listeners[event].includes(handler)) {
      this.$listeners[event].push(handler);
    }

    return () => this.$off(event, handler);
  }

  $off(event, handler) {
    if (!this.$listeners[event]) return;

    const index = this.$listeners[event].indexOf(handler);
    if (index > -1) {
      this.$listeners[event].splice(index, 1);
    }
  }

  $initObserver() {
    if (!window.MutationObserver) {
      console.warn('Current browser doesn\'t support MutationObserver. You should call this.$updateRefs() manually on each DOM change.');
      return;
    }

    if (this.$observer) return;

    this.$observer = new MutationObserver(_throttle((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const shouldUpdateRefs = [
            ...mutation.removedNodes,
            ...mutation.addedNodes,
          ].some((node) => (
            node.nodeType === Node.ELEMENT_NODE && node.matches(`[${REF_ATTR}^="${this.$name}${REF_SEPARATOR}"]`)
          ));

          if (shouldUpdateRefs) {
            this.$updateRefs();
          }
        }
      });
    }, 200));

    this.$observer.observe(this.$el, {
      childList: true,
      subtree: true,
    });
  }
}

export default Component;
